import { Divider, MenuItem, Switch } from '@mui/material';
import type { BillingInterval } from '@weft/types';
import { BILLING_INTERVALS } from '@weft/types';
import { entries, isNotNullOrUndefined } from '@weft/utils';
import { get } from 'lodash';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { VayuTextField } from '../../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useFormErrorNotificationContext } from '../../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useChargeContext } from '../../../charge-context';
import type { NewPlanForm } from '../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { ChargeEditDrawerContentItemOptional } from '../ChargeEditDrawerContentItemOptional';
import { PaymentTermDropdown } from '../components/PaymentTermDropdown';
import { ProductErpIdSelector } from '../components/ProductErpIdSelector';

export const CommercialTermsChargeEditDrawerContent: React.FC = () => {
  const { itemIndex: productIndex } = useChargeContext();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { watch, setValue, register, control, formState: { errors } } = useFormContext<PlanInputForm, NewPlanForm>();

  const registerDescription = register(`fixedProducts.${productIndex}.variants.0.description`);
  registerErrorField(registerDescription.name, CREATE_PLAN_TABS.serviceFees);

  return (
    <div className="flex flex-col gap-y-6">
      <ChargeEditDrawerContentItemOptional
        fieldPath={registerDescription.name}
        label="Add description"
      >
        <VayuTextField
          {...registerDescription}
          fullWidth
          focused
          placeholder="Add a description"
          multiline
          error={isNotNullOrUndefined(get(errors, registerDescription.name))}
        />
      </ChargeEditDrawerContentItemOptional>

      <Divider/>

      <div className="flex items-center gap-x-2">
        <Controller
          control={control}
          name={`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.shouldCreateSeparateInvoice`}
          render={({ field }) => (
            <Switch
              checked={field.value}
              {...field}
              onChange={event => {
                field.onChange(event);

                if (!event.target.checked) {
                  setValue(`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.shouldSplitAmount`, false);
                }
              }}
            />
          )}
        />
        <label>Issue as separate invoice</label>
      </div>

      <Divider/>

      <div className="flex items-center gap-x-2">
        <PaymentTermDropdown
          productIndex={productIndex}
          control={control}
        />
      </div>

      <Divider/>

      <Controller
        control={control}
        name={`fixedProducts.${productIndex}.billingInterval`}
        render={({ field }) => (
          <VayuTextField
            label="Billing interval"
            placeholder="Select billing interval"
            select
            fullWidth
            focused
            {...field}
          >
            {entries(BILLING_INTERVALS).map(([key, value]) => (
              <MenuItem
                key={key}
                value={value}
                selected={value === watch(`fixedProducts.${productIndex}.billingInterval`)}
              >
                {getBillingIntervalLabel(value)}
              </MenuItem>
            ))}
          </VayuTextField>
        )}
      />

      <Divider/>

      <div className="flex items-center gap-x-2">
        <Controller
          control={control}
          name={`fixedProducts.${productIndex}.variants.0.pricing.priceConfiguration.shouldSplitAmount`}
          render={({ field }) => (
            <Switch
              checked={field.value}
              {...field}
            />
          )}
        />
        <label>Split amount based on interval</label>
      </div>

      <ProductErpIdSelector />
    </div>
  );
};

function getBillingIntervalLabel(billingInterval: BillingInterval) {
  const billingIntervals: Record<BillingInterval, string> = {
    [BILLING_INTERVALS.Monthly]: 'Monthly',
    [BILLING_INTERVALS.BiMonthly]: 'Bi monthly',
    [BILLING_INTERVALS.Quarterly]: 'Quarterly',
    [BILLING_INTERVALS.SixMonths]: 'Every 6 months',
    [BILLING_INTERVALS.Yearly]: 'Yearly',
  };

  return billingIntervals[billingInterval];
}